import React, { useEffect, useMemo, useRef, useState } from "react";
import BrandsService from "../../../services/brand.service";
import { BrandEvent } from "../../../models/brandEvent";
import Dropdown from "../Dropdown/Dropdown";

interface EventHashtagSelectProps {
    selectedEventTags: string[];
    addEventTag: (tag: string) => void;
    removeEventTag: (tag: string) => void;
}

const EventHashtagSelect: React.FC<EventHashtagSelectProps> = ({
    selectedEventTags,
    addEventTag,
    removeEventTag,
}) => {
    const [events, setEvents] = useState<BrandEvent[]>([]);
    const [userInput, setUserInput] = useState<string>("");
    const [dropdown, setDropdown] = useState<boolean>(false);

    const closeDropdown = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchEvents = async () => {
            const allEvents = await BrandsService.getAllEvents();
            if (allEvents) {
                const now = new Date();
                const active = allEvents.filter((event: BrandEvent) => {
                    const start = new Date(event.start_date);
                    const end = new Date(event.end_date);
                    return start <= now && end >= now && event.event_tag;
                });
                setEvents(active);
            }
        };
        fetchEvents();
    }, []);

    useEffect(() => {
        const closeOpenMenus = (e: MouseEvent) => {
            if (closeDropdown.current && dropdown && !closeDropdown.current.contains(e.target as Node)) {
                setDropdown(false);
            }
        };
        document.addEventListener("mousedown", closeOpenMenus);
        return () => {
            document.removeEventListener("mousedown", closeOpenMenus);
        };
    }, [dropdown]);

    const availableOptions = useMemo(() => {
        const tags = events
            .map((event) => event.event_tag.startsWith("#") ? event.event_tag : `#${event.event_tag}`)
            .filter((tag) => !selectedEventTags.includes(tag));

        if (userInput) {
            return tags.filter((tag) => tag.toLowerCase().includes(userInput.toLowerCase()));
        }
        return tags;
    }, [events, selectedEventTags, userInput]);

    const addItem = (item: string) => {
        addEventTag(item);
        setUserInput("");
        setDropdown(false);
    };

    if (events.length === 0) {
        return null;
    }

    return (
        <div className="autcomplete-wrapper" ref={closeDropdown} onClick={() => setDropdown(true)}>
            <div className="autcomplete">
                <div className="w-full flex flex-col items-center mx-auto">
                    <div className="w-full">
                        <div className="flex flex-col items-center relative">
                            <div className="w-full">
                                <div className="my-2 p-1 flex shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5">
                                    <div className="flex flex-auto flex-wrap">
                                        {selectedEventTags.map((tag, index) => (
                                            <div
                                                key={index}
                                                className="flex justify-center items-center m-1 font-medium py-1 px-2 bg-white rounded-full text-teal-700 bg-teal-100 border border-teal-300"
                                            >
                                                <div className="text-xs font-normal leading-none max-w-full flex-initial">
                                                    {tag}
                                                </div>
                                                <div className="flex flex-auto flex-row-reverse">
                                                    <div onClick={() => removeEventTag(tag)}>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="100%"
                                                            height="100%"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            className="feather feather-x cursor-pointer hover:text-teal-400 rounded-full w-4 h-4 ml-2"
                                                        >
                                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="flex-1">
                                            <input
                                                value={userInput}
                                                placeholder="Select an active event"
                                                className="bg-transparent p-1 px-2 appearance-none outline-none h-full w-full text-gray-800"
                                                onChange={(e) => {
                                                    setUserInput(e.target.value);
                                                    if (e.target.value.length > 0 && availableOptions.length > 0) {
                                                        setDropdown(true);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {dropdown && availableOptions.length > 0 ? (
                            <Dropdown list={availableOptions} addItem={addItem} />
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventHashtagSelect;
